import { applyStoragePreference, configureAmplify, writeSessionOnlyPreference } from './amplify';
import { api } from '../api';
import { loginUrlReturningHere } from './redirect';

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: 'platform_admin' | 'organization' | 'user' | string;
  /** FK to <c>Organization.Id</c>. Set only for organization-role users - platform_admin gets null. */
  organizationId: string | null;
}

const MANAGEMENT_ROLES = ['platform_admin', 'organization'] as const;
export function isManagementRole(role: string): boolean {
  return (MANAGEMENT_ROLES as readonly string[]).includes(role);
}

export function isPlatformAdmin(role: string): boolean {
  return role === 'platform_admin';
}

/**
 * Resolves the signed-in user's profile from the backend (creating a local
 * "user"-role record on first call). Throws if not signed in.
 */
export async function getCurrentUser(): Promise<CurrentUser> {
  await configureAmplify();
  const client = await api();
  const { data } = await client.get<CurrentUser>('/api/users/me');
  return data;
}

/**
 * Guards an admin page. Redirects to the login page if not signed in or
 * the user has no management role (platform_admin or organization). Returns the
 * current user when access is granted.
 */
export async function requireAdmin(loginUrl = '/login.html'): Promise<CurrentUser> {
  try {
    const me = await getCurrentUser();
    if (!isManagementRole(me.role)) {
      window.location.replace(loginUrl + '?error=not-admin');
      throw new Error('not admin');
    }
    return me;
  } catch (err) {
    if (!(err instanceof Error) || err.message !== 'not admin') {
      // Not signed in (or session expired): come back here after login, so a
      // deep link such as the public map's usage-history button still lands.
      window.location.replace(loginUrlReturningHere(loginUrl));
    }
    throw err;
  }
}

/**
 * Stricter guard for the Management group of admin pages (users, organizations,
 * applications, contact messages, audit logs, app settings,
 * email templates, installation parties). Organizations are signed in but
 * forbidden - bounced back to the dashboard rather than the login page so
 * they don't see a misleading "please sign in" prompt.
 */
export async function requirePlatformAdmin(redirectIfOrganization = '/admin.html'): Promise<CurrentUser> {
  const me = await requireAdmin();
  if (!isPlatformAdmin(me.role)) {
    window.location.replace(redirectIfOrganization);
    throw new Error('not platform admin');
  }
  return me;
}

export interface SignInResult {
  /** True when Cognito issued tokens - caller can proceed to the post-login destination. */
  isSignedIn: boolean;
  /**
   * True when Cognito returned the FORCE_CHANGE_PASSWORD challenge: the account was
   * created by an admin and the user must replace the temp password before tokens are
   * issued. Caller should prompt for a new password and call <see cref="completeNewPassword"/>.
   */
  requiresNewPassword: boolean;
  /**
   * True when Cognito demands a confirmation-code-driven reset (admin force-reset).
   * Caller should kick the user into the forgot-password flow.
   */
  requiresPasswordReset: boolean;
}

export async function signIn(email: string, password: string, remember = true): Promise<SignInResult> {
  await configureAmplify();
  // Per-session storage when "Remember me" is unchecked: tokens live in
  // sessionStorage, so they vanish when the tab/browser closes. Default
  // (true) keeps Amplify's localStorage which persists across restarts.
  // Persist the preference so the next page load (e.g. /admin.html) reads
  // tokens from the same backing store - without this, fetchAuthSession()
  // on the next page sees an empty default-storage and returns no token.
  const sessionOnly = !remember;
  writeSessionOnlyPreference(sessionOnly);
  await applyStoragePreference(sessionOnly);
  const { signIn: amplifySignIn } = await import('aws-amplify/auth');
  const result = await amplifySignIn({ username: email, password });
  const step = result.nextStep?.signInStep;
  return {
    isSignedIn: result.isSignedIn,
    requiresNewPassword: step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
    requiresPasswordReset: step === 'RESET_PASSWORD',
  };
}

/**
 * Completes the FORCE_CHANGE_PASSWORD challenge by submitting the user's chosen
 * new password to Cognito's <c>respondToAuthChallenge</c>. The pending challenge
 * is held in Amplify's session state - must be called immediately after the
 * <see cref="signIn"/> that returned <c>requiresNewPassword</c>.
 */
export async function completeNewPassword(newPassword: string): Promise<void> {
  await configureAmplify();
  const { confirmSignIn } = await import('aws-amplify/auth');
  await confirmSignIn({ challengeResponse: newPassword });
}

/**
 * Maps a Cognito/Amplify exception to a translation key under <c>inloggen.errors.*</c>.
 * Returns <c>generic</c> when the error doesn't match a known case so callers can
 * still render *something* visible.
 */
export function cognitoErrorKey(err: unknown): string {
  const name = (err as { name?: string } | null)?.name ?? '';
  switch (name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      // Don't disclose which one - same friendly message either way.
      return 'inloggen.errors.invalid_credentials';
    case 'UserNotConfirmedException':
      return 'inloggen.errors.not_confirmed';
    case 'PasswordResetRequiredException':
      return 'inloggen.errors.reset_required';
    case 'InvalidPasswordException':
      return 'inloggen.errors.weak_password';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'inloggen.errors.rate_limited';
    case 'CodeMismatchException':
      return 'inloggen.errors.bad_code';
    case 'ExpiredCodeException':
      return 'inloggen.errors.expired_code';
    case 'NetworkError':
      return 'inloggen.errors.network';
    case 'EmptySignInUsername':
    case 'EmptySignInPassword':
      return 'inloggen.errors.empty_fields';
    default:
      return 'inloggen.errors.generic';
  }
}

/** Starts the Cognito password-reset flow. Sends a confirmation code to the user's email. */
export async function resetPassword(email: string): Promise<void> {
  await configureAmplify();
  const { resetPassword: amplifyReset } = await import('aws-amplify/auth');
  await amplifyReset({ username: email });
}

/** Completes the Cognito password-reset flow with the email-delivered code + new password. */
export async function confirmResetPassword(email: string, code: string, newPassword: string): Promise<void> {
  await configureAmplify();
  const { confirmResetPassword: amplifyConfirm } = await import('aws-amplify/auth');
  await amplifyConfirm({ username: email, confirmationCode: code, newPassword });
}

export async function signOut(): Promise<void> {
  await configureAmplify();
  const { signOut: amplifySignOut } = await import('aws-amplify/auth');
  await amplifySignOut();
  // Reset Remember-me so the next sign-in starts from the default (localStorage).
  writeSessionOnlyPreference(false);
  await applyStoragePreference(false);
}

export interface SignUpResult {
  /** True when Cognito accepted the sign-up immediately (rare; usually a confirmation code is required first). */
  isComplete: boolean;
}

/**
 * Creates a new Cognito user. Most pools are configured to require a
 * confirmation code, in which case the caller should immediately collect
 * the code from the user's email and call <see cref="confirmSignUp"/>.
 */
export async function signUp(email: string, password: string, name: string): Promise<SignUpResult> {
  await configureAmplify();
  const { signUp: amplifySignUp } = await import('aws-amplify/auth');
  const { isSignUpComplete } = await amplifySignUp({
    username: email,
    password,
    options: { userAttributes: { email, name } },
  });
  return { isComplete: isSignUpComplete };
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  await configureAmplify();
  const { confirmSignUp: amplifyConfirm } = await import('aws-amplify/auth');
  await amplifyConfirm({ username: email, confirmationCode: code });
}
